"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"

interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  status: string
  attachments: string[]
  institution?: string
  location?: string
}

const MyCalendar = observer(() => {
  const { store, api, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedView, setSelectedView] = useState<"month" | "list">("month")

  const me = store.auth.me?.asJson

  const allTrainings = store.trainingRequest.all
    .map((training) => training.asJson)
    .filter((training) => training.uid === me?.uid)

  const events: CalendarEvent[] = allTrainings.map((training) => ({
    id: training.id,
    title: training.courseName || "Training",
    start: new Date(training.startDate),
    end: new Date(training.endDate),
    status: training.status,
    attachments: training.attachment || [],
    institution: training.institution,
    location: training.courseLocation,
  }))

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    return { daysInMonth, startingDayOfWeek, year, month }
  }

  const getEventsForDay = (day: number) => {
    const { year, month } = getDaysInMonth(currentDate)
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      const checkDate = new Date(year, month, day)

      return (
        checkDate >= new Date(eventStart.setHours(0, 0, 0, 0)) &&
        checkDate <= new Date(eventEnd.setHours(23, 59, 59, 999))
      )
    })
  }

  const handleViewAttachments = (attachments: string[]) => {
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        window.open(attachment, "_blank", "noopener,noreferrer")
      })
    } else {
      ui.snackbar.load({
        id: Date.now(),
        message: "No attachments available.",
        type: "warning",
      })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-500"
      case "pending":
        return "bg-yellow-500"
      case "rejected":
        return "bg-red-500"
      default:
        return "bg-gray-500"
    }
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1)
      } else {
        newDate.setMonth(newDate.getMonth() + 1)
      }
      return newDate
    })
  }

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        await api.trainingRequest.getAll()
        await api.department.getAll()
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }

    loadAll()
  }, [api.trainingRequest, api.department, ui.snackbar])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading calendar...</div>
      </div>
    )
  }

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate)
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1)
  const emptyDays = Array.from({ length: startingDayOfWeek }, (_, i) => i)

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#84cbe9] to-[#3f6a8e] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => navigateMonth("prev")}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => navigateMonth("next")}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex gap-2 bg-white/20 rounded-lg p-1">
              <button
                onClick={() => setSelectedView("month")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedView === "month" ? "bg-white text-[#3f6a8e]" : "text-white hover:bg-white/20"
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setSelectedView("list")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedView === "list" ? "bg-white text-[#3f6a8e]" : "text-white hover:bg-white/20"
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {selectedView === "month" ? (
          <div className="p-6">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center font-semibold text-gray-600 text-sm py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-2">
              {emptyDays.map((i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}

              {calendarDays.map((day) => {
                const dayEvents = getEventsForDay(day)
                const isToday =
                  day === new Date().getDate() &&
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear()

                return (
                  <div
                    key={day}
                    className={`aspect-square border rounded-lg p-2 ${
                      isToday ? "border-[#84cbe9] bg-blue-50" : "border-gray-200"
                    } hover:border-[#84cbe9] transition-colors`}
                  >
                    <div className={`text-sm font-semibold mb-1 ${isToday ? "text-[#3f6a8e]" : "text-gray-700"}`}>
                      {day}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs px-2 py-1 rounded ${getStatusColor(event.status)} text-white truncate`}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-gray-500 px-2">+{dayEvents.length - 2} more</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="p-6">
            {events.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No training events scheduled</p>
              </div>
            ) : (
              <div className="space-y-4">
                {events
                  .sort((a, b) => a.start.getTime() - b.start.getTime())
                  .map((event) => (
                    <div
                      key={event.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-[#84cbe9] transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                            <span
                              className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getStatusColor(event.status)}`}
                            >
                              {event.status}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>
                              <span className="font-medium">Dates:</span> {event.start.toLocaleDateString()} -{" "}
                              {event.end.toLocaleDateString()}
                            </p>
                            {event.institution && (
                              <p>
                                <span className="font-medium">Institution:</span> {event.institution}
                              </p>
                            )}
                            {event.location && (
                              <p>
                                <span className="font-medium">Location:</span> {event.location}
                              </p>
                            )}
                          </div>
                        </div>
                        {event.attachments && event.attachments.length > 0 && (
                          <button
                            onClick={() => handleViewAttachments(event.attachments)}
                            className="px-4 py-2 bg-[#e7a637] text-white font-medium rounded-lg hover:bg-[#d69520] transition-colors duration-200 text-sm"
                          >
                            View Attachments ({event.attachments.length})
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

export default MyCalendar
